<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed } from "vue";

import { HospitalityButtonSeverity } from "@/components/ui/buttons/hospitality-button.types";
import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { V4HSiteId } from "@/data/common/ids";
import { useTypedIntl } from "@/locales/typed-intl";
import { useContactVoltalisDialogStore } from "@/stores/contact-voltalis-dialog/contact-voltalis-dialog-store";
import { branded } from "@/utils/branded-validation-utils";
import { useContactUrl } from "@/utils/support-utils";

import Skeleton from "primevue/skeleton";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  isCollapsed?: boolean;
  severity?: HospitalityButtonSeverity;
}

interface MultisiteProps {
  "aria-controls": string;
  "aria-expanded": boolean;
  "aria-haspopup": "dialog";
  "aria-label": string;
  "data-test-id": string;
  class: string | undefined;
  onClick: () => void;
}

const props = defineProps<IProps>();

defineSlots<{
  monosite(): unknown;
  multisite(props: { multisiteProps: MultisiteProps }): unknown;
}>();

const contactVoltalisStore = useContactVoltalisDialogStore();
const { visible: isContactVoltalisDialogVisible } = storeToRefs(contactVoltalisStore);

const { data: sites, isLoading } = useGetSitesQuery();

const { t, k } = useTypedIntl();

const multisiteProps = computed<MultisiteProps>(() => ({
  "aria-controls": "contact-voltalis-dialog",
  "aria-expanded": isContactVoltalisDialogVisible.value,
  "aria-haspopup": "dialog" as const,
  "aria-label": t(k.components.layout.sideMenu.contact),
  "data-test-id": "contact-link",
  class: props.isCollapsed ? "w-[48px] flex justify-center" : undefined,
  onClick: () => (isContactVoltalisDialogVisible.value = true),
}));

const monositeId = computed<V4HSiteId | undefined>(() => {
  const sitesArr = sites.value ?? [];
  const [firstSite] = sitesArr;

  if (!firstSite || sitesArr.length > 1) return;

  return branded.v4hSiteId.parse(firstSite.siteId);
});

const { contactUrl } = useContactUrl(monositeId);
</script>

<template>
  <Skeleton
    class="!h-[52px]"
    v-if="isLoading"
  />

  <template v-else>
    <a
      target="_blank"
      v-if="monositeId"
      :aria-label="t(k.components.layout.sideMenu.contact)"
      :class="{
        'w-[48px] flex justify-center': props.isCollapsed,
      }"
      data-test-id="contact-link"
      :href="contactUrl"
    >
      <slot name="monosite">
        <HospitalityButton
          :aria-label="t(k.components.layout.sideMenu.contact)"
          :label="t(k.components.layout.sideMenu.contact)"
          :severity="props.severity ?? 'secondary'"
          tabindex="0"
        />
      </slot>
    </a>
    <template v-else>
      <slot
        name="multisite"
        :multisiteProps="multisiteProps"
      >
        <HospitalityButton
          v-bind="multisiteProps"
          :label="t(k.components.layout.sideMenu.contact)"
          :severity="props.severity ?? 'secondary'"
          @click="isContactVoltalisDialogVisible = true"
        />
      </slot>
    </template>
  </template>
</template>
